import { useCallback, useEffect, useRef } from "react"

/**
 * Measurement for the animated hero and 404 art.
 *
 * One hook, both surfaces. The 404 is the control the hero never has: the same
 * art, the same events, but the visitor arrived by accident. If a variant lifts
 * engagement on the hero and not on the 404, the lift is the copy and the offer,
 * not the animation. That comparison is the whole point of instrumenting both.
 *
 * GA4 is already on the page (G-8Y22TR6NEN, production only - see index.html),
 * so events go to gtag. Every call is a no-op in dev and in SSR.
 */

type Params = Record<string, string | number | boolean | undefined>

declare global {
  interface Window {
    gtag?: (command: string, eventName: string, params?: Params) => void
    dataLayer?: unknown[]
  }
}

export const track = (event: string, params: Params = {}): void => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return
  window.gtag("event", event, params)
}

/** Sticky per-visitor bucket, so a returning visitor keeps seeing one variant. */
const BUCKET_KEY = "mab-hero-variant"

export const assignVariant = (variants: string[]): string => {
  if (typeof window === "undefined") return variants[0]
  try {
    const held = window.localStorage.getItem(BUCKET_KEY)
    if (held && variants.includes(held)) return held
    const pick = variants[Math.floor(Math.random() * variants.length)]
    window.localStorage.setItem(BUCKET_KEY, pick)
    return pick
  } catch {
    return variants[0]
  }
}

export interface HeroAnalyticsOptions {
  /** Which art is on screen, e.g. "orchestration". */
  variant: string
  /** "hero" or "404" - the same variant is measured on both. */
  surface: "hero" | "404"
  /** Dwell thresholds in seconds. Each fires once. */
  dwellMarks?: number[]
}

/**
 * Returns a ref to attach to the art's container plus a click reporter.
 *
 * Emits:
 *   hero_impression  - art reached 50% visible (the denominator for every rate)
 *   hero_engage      - first pointer move over the art (did the motion invite touch?)
 *   hero_dwell       - crossed 3s / 10s / 30s in view (attention, not just arrival)
 *   hero_cta         - a call to action under the art was clicked (the conversion)
 *   hero_exit        - left with time-in-view and whether they ever engaged
 */
export const useHeroAnalytics = ({
  variant,
  surface,
  dwellMarks = [3, 10, 30],
}: HeroAnalyticsOptions) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const seen = useRef(false)
  const engaged = useRef(false)
  const enteredAt = useRef(0)
  const total = useRef(0)
  const fired = useRef<Set<number>>(new Set())

  const base = useCallback(
    (): Params => ({ variant, surface }),
    [variant, surface]
  )

  useEffect(() => {
    const el = ref.current
    if (!el) return undefined

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          enteredAt.current = performance.now()
          if (!seen.current) {
            seen.current = true
            track("hero_impression", base())
          }
        } else if (enteredAt.current) {
          total.current += (performance.now() - enteredAt.current) / 1000
          enteredAt.current = 0
        }
      },
      { threshold: 0.5 }
    )
    io.observe(el)

    const onMove = () => {
      if (engaged.current) return
      engaged.current = true
      track("hero_engage", { ...base(), ms_to_engage: Math.round(performance.now() - enteredAt.current) })
    }
    el.addEventListener("pointermove", onMove, { once: true })

    const dwell = window.setInterval(() => {
      if (!enteredAt.current) return
      const secs = total.current + (performance.now() - enteredAt.current) / 1000
      for (const mark of dwellMarks) {
        if (secs >= mark && !fired.current.has(mark)) {
          fired.current.add(mark)
          track("hero_dwell", { ...base(), seconds: mark })
        }
      }
    }, 1000)

    const onExit = () => {
      const secs = total.current + (enteredAt.current ? (performance.now() - enteredAt.current) / 1000 : 0)
      if (!seen.current) return
      track("hero_exit", {
        ...base(),
        seconds_in_view: Math.round(secs),
        engaged: engaged.current,
      })
    }
    window.addEventListener("pagehide", onExit)

    return () => {
      io.disconnect()
      el.removeEventListener("pointermove", onMove)
      window.clearInterval(dwell)
      window.removeEventListener("pagehide", onExit)
    }
  }, [base, dwellMarks])

  const trackCta = useCallback(
    (label: string) => track("hero_cta", { ...base(), label }),
    [base]
  )

  return { ref, trackCta }
}
